import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Key, Plus, Pencil, Trash2, Save, X, RefreshCw, ChevronLeft, ChevronRight, Eye, Search, GripVertical } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi } from '../../services/api';

const PermissionsPage = () => {
  const [permissions, setPermissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminManagementApi.getPermissions();
      setPermissions(res.data);
    } catch (err) {
      console.error('Failed to load permissions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!selected) return;
    setEditing({ ID: selected.id, PermissionKey: selected.permissionKey, Category: selected.category, DisplayName: selected.displayName, Description: selected.description || '' });
    setDirtyFields(new Set());
  };

  const startAdd = () => {
    setSelected(null);
    setEditing({ PermissionKey: '', Category: 'Module', DisplayName: '', Description: '' });
    setDirtyFields(new Set(['PermissionKey', 'Category', 'DisplayName']));
  };

  const cancelEdit = (force = false) => {
    if (!force && dirtyFields.size > 0) { notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyFields(new Set()); }); return; }
    setEditing(null); setDirtyFields(new Set());
  };

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const original = selected ? (field === 'Description' ? (selected.description || '') : selected[field.charAt(0).toLowerCase() + field.slice(1)]) : '';
    const newDirty = new Set(dirtyFields);
    if (value !== original || !selected) { newDirty.add(field); } else { newDirty.delete(field); }
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || dirtyFields.size === 0) return;
    // Duplicate key check on new permissions
    if (!editing.ID) {
      const dupe = permissions.find(p => p.permissionKey.toLowerCase() === editing.PermissionKey.trim().toLowerCase());
      if (dupe) { notify.error(`Permission key "${editing.PermissionKey}" already exists.`); return; }
    }
    setSaving(true);
    try {
      if (editing.ID) {
        await adminManagementApi.updatePermission(editing.ID, editing);
      } else {
        await adminManagementApi.createPermission(editing);
      }
      const savedId = editing.ID;
      setEditing(null);
      setDirtyFields(new Set());
      const res = await adminManagementApi.getPermissions();
      setPermissions(res.data);
      if (savedId) { const refreshed = res.data.find(p => p.id === savedId); if (refreshed) setSelected(refreshed); }
      else setSelected(null);
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = () => {
    if (!editing?.ID) { cancelEdit(); return; }
    notify.confirm(`Delete permission "${editing.PermissionKey}"? This removes it from all role definitions and user permissions.`, async () => {
      setSaving(true);
      try {
        await adminManagementApi.deletePermission(editing.ID);
        setEditing(null); setSelected(null); setDirtyFields(new Set());
        await load();
      } catch (err) { console.error('Delete failed:', err); }
      finally { setSaving(false); }
    }, { onCancel: () => cancelEdit() });
  };

  const isEditing = editing !== null;
  const hasDirty = dirtyFields.size > 0;

  const filtered = permissions.filter(p =>
    !searchTerm ||
    (p.displayName || p.permissionKey).toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="dev-page loading">Loading...</div>;

  return (
    <div className="module-container">
      <div className="module-header">
        <div className="module-header-left">
          <Key size={20} style={{ color: 'var(--color-primary)' }} />
          <h1>Permissions List</h1>
          <span className="module-header-meta">{permissions.length} permissions</span>
        </div>
        <div className="module-header-actions">
          {isEditing ? (
            <button className="crud-btn" onClick={handleDelete} disabled={saving} title="Delete" style={{ color: 'var(--color-error)' }}><Trash2 size={18} /></button>
          ) : (
            <button className="crud-btn" onClick={startAdd} title="Add permission"><Plus size={18} /></button>
          )}
          <button className={`crud-btn ${!isEditing && !selected ? 'disabled' : ''}`} onClick={isEditing ? cancelEdit : startEdit} disabled={!selected && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
            {isEditing ? <X size={18} /> : <Pencil size={18} />}
          </button>
          <button className={`crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
          <div className="action-menu" ref={menuRef}>
            <button className="action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="action-menu-dropdown">
                <button className="action-menu-item" onClick={() => { setMenuOpen(false); load(); }}><RefreshCw size={16} /><span>Refresh</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="list-view">
        {listCollapsed ? (
          <div className="list-panel-collapsed">
            <button onClick={() => setListCollapsed(false)} className="list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
          </div>
        ) : (
          <div className="list-panel">
            <div className="list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              <div style={{ flex: 1, position: 'relative' }}>
                <Search size={14} style={{ position: 'absolute', left: '8px', top: '10px', color: 'var(--color-gray-400)' }} />
                <input className="form-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search permissions..." style={{ paddingLeft: '28px', width: '100%' }} />
              </div>
            </div>
            <div className="list-scroll">
              {filtered.length === 0 ? (
                <div className="task-list-empty">No permissions found</div>
              ) : filtered.map(p => (
                <button key={p.id} onClick={() => { setSelected(p); setEditing(null); setDirtyFields(new Set()); }} className={`list-item-btn ${selected?.id === p.id ? 'selected' : ''}`}>
                  
                    <span className="list-item-primary">{p.displayName || p.permissionKey}</span>
                    <span className="list-item-secondary">{p.category} • {p.permissionKey}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="detail-panel">
          {isEditing ? (
            <div className="detail-content">
              <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                <div className="col-span-24"><div className="detail-section-header">{editing.ID ? 'Edit Permission' : 'New Permission'}</div></div>
                <div className="form-group col-span-8">
                  <label>Category</label>
                  <select className={`form-input${dirtyFields.has('Category') ? ' dirty' : ''}`} value={editing.Category} onChange={e => handleFieldChange('Category', e.target.value)}>
                    <option value="Module">Module</option><option value="Action">Action</option><option value="Feature">Feature</option><option value="Admin">Admin</option><option value="Development">Development</option>
                  </select>
                </div>
                <div className="form-group col-span-8">
                  <label>Display Name</label>
                  <input className={`form-input${dirtyFields.has('DisplayName') ? ' dirty' : ''}`} value={editing.DisplayName} onChange={e => handleFieldChange('DisplayName', e.target.value)} />
                </div>
                <div className="form-group col-span-8">
                  <label>Permission Key</label>
                  {editing.ID ? (
                    <input className="form-input derived-field" value={editing.PermissionKey} readOnly title="Key is immutable after creation" />
                  ) : (
                    <input className={`form-input${dirtyFields.has('PermissionKey') ? ' dirty' : ''}`} value={editing.PermissionKey} onChange={e => handleFieldChange('PermissionKey', e.target.value)} placeholder="e.g. module.submittal" />
                  )}
                </div>
                <div className="form-group col-span-24">
                  <label>Description</label>
                  <textarea className={`form-input${dirtyFields.has('Description') ? ' dirty' : ''}`} value={editing.Description} onChange={e => handleFieldChange('Description', e.target.value)} rows={3} />
                </div>
              </div>
            </div>
          ) : selected ? (
            <div className="detail-content">
              <div className="detail-grid-24" style={{ flexShrink: 0 }}>
                <div className="col-span-24"><div className="detail-section-header">Permission Details</div></div>
                <div className="form-group col-span-8"><label>Category</label><input className="form-input" value={selected.category || ''} readOnly /></div>
                <div className="form-group col-span-8"><label>Display Name</label><input className="form-input" value={selected.displayName || ''} readOnly /></div>
                <div className="form-group col-span-8"><label>Permission Key</label><input className="form-input derived-field" value={selected.permissionKey} readOnly /></div>
                {selected.description && <div className="form-group col-span-24"><label>Description</label><input className="form-input" value={selected.description} readOnly /></div>}
              </div>
            </div>
          ) : (
            <div className="dev-detail-empty"><Eye size={48} /><p>Select a permission to view details</p><p className="hint">Or click + to define a new one</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PermissionsPage;
